/**
 * <StudentCoachPage> — l'onglet « Messages » de l'espace élève (page).
 *
 * L'ordre de l'écran d'aujourd'hui (`MyCoachTabScreen.js`), dans la forme du DS :
 *  1. pourquoi l'élève n'a pas de tuteur, quand il n'en a pas (`noTutor`) ;
 *  2. l'offre Essentielle, pour qui peut démarrer un essai (`onStartTrial`) — ou, abonnement en
 *     pause, la même carte qui dit la pause (`onAskQuestion`). La phrase de Tina qui suivait
 *     l'offre est retirée (Wajih, 13/10) : elle écrivait « championne » et posait Tina là où elle
 *     n'agit pas ;
 *  3. « Mon tuteur », ou « Mes tuteurs » quand il en a plusieurs (Wajih, 16/10) — ses tuteurs,
 *     un par ligne : la ligne ouvre SA conversation, avec ses non-lus.
 * Sans aucun tuteur, il n'y a pas d'équipe à montrer : 3 n'est pas monté.
 *
 * Les prochains lives ont quitté l'onglet (Wajih, 18/10) : ils sont sur l'accueil, et tous, avec
 * leurs gestes (« GO ! », décaler, prévenir d'une absence, signaler un retard, « Planifier un
 * rattrapage »), dans « Tous mes lives » (`<LiveSessionsListPage voice="student">`). « Messages »
 * garde les tuteurs et leurs conversations.
 *
 * Les champions n'entrent pas dans l'équipe (Wajih, 15/10) : ils se montrent là où ils agissent,
 * et leurs quêtes vivent sur l'accueil et leurs écrans.
 *
 * Dans le volet gauche d'un gabarit à deux volets (`<StudentCoachTwoPane>`), la page se borne à la
 * largeur du volet (`columnWidth`) et marque le tuteur dont la conversation est ouverte
 * (`openTutorId`).
 */
import React from "react"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { type IconName } from "../../atoms/icons/Icon"
import { Title } from "../../atoms/typography/Title"
import { EmptyState } from "../../molecules/EmptyState"
import { HighlightFrame } from "../../molecules/HighlightFrame"
import { RefreshButton } from "../../molecules/RefreshButton"
import { TrialOfferCard, type UIPausePeriod } from "../../molecules/TrialOfferCard"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { TutorTeamSection, type UIStudentTutor } from "../../templates/TutorTeamSection"
import { formatDayMonthYear } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type UITutor } from "../../../ports/primary/ui/types"
import { type UIStudentNoTutor } from "../../../ports/primary/ui/types/studentTutoring"
import { StudentCoachSkeleton } from "./StudentCoachSkeleton"

export type { UIStudentNoTutor }

type NoTutorTexts = { iconName?: IconName; title: string; message?: string }

function noTutorTexts(state: UIStudentNoTutor): NoTutorTexts {
    switch (state.kind) {
        case "awaiting":
            return {
                iconName: "hourglass-half",
                title: "Tu recevras une notification dès que nous t'aurons attribué un groupe de Live et un tuteur.",
            }
        case "unsubscribed":
            return {
                title: "Tu n'as pas d'abonnement actif.",
                message:
                    "Pour avoir un tuteur et participer aux Lives Masteur, il faut être abonné à l'une de nos offres.",
            }
        case "paused": {
            const resume = "Tu pourras accéder à ce menu lors de la reprise de ton abonnement"
            return {
                title: "Ton abonnement est en pause.",
                message:
                    state.resumeDate != null
                        ? `${resume}, le ${formatDayMonthYear(state.resumeDate, false)}.`
                        : `${resume}.`,
            }
        }
    }
}

export type StudentCoachPageProps = {
    /** Les tuteurs de l'élève — le principal d'abord, puis ceux de ses autres séances. */
    tutors: UIStudentTutor[]
    /** Ouvre la conversation avec CE tuteur. ABSENT = pas de messagerie : les lignes ne pressent pas. */
    onOpenMessages?: (tutor: UITutor) => void
    /** Le tuteur dont la conversation est ouverte à côté (deux volets). Défaut : aucun. */
    openTutorId?: UITutor["id"] | null
    /** La largeur de la colonne, dans un volet. Défaut : celle de l'espace (`PARENT_CONTENT_WIDTH`). */
    columnWidth?: number
    /** L'élève n'a pas de tuteur principal : pourquoi. Absent = il en a un. */
    noTutor?: UIStudentNoTutor
    /** « Essai gratuit ». ABSENT = pas d'offre à montrer (l'élève est abonné, ou n'y a pas droit). */
    onStartTrial?: () => void
    /**
     * « Une question ? », sur la carte « Abonnement en pause » qui prend la place de l'offre.
     * ABSENT = l'abonnement n'est pas en pause.
     */
    onAskQuestion?: () => void
    /** La période de la pause, écrite sur cette carte. */
    pausePeriod?: UIPausePeriod

    // ── État de page ──
    loading?: boolean
    /** Une relecture est en cours : le bouton de la barre de titre tourne. */
    refreshing?: boolean
    onRefresh?: () => void
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function StudentCoachPage(props: StudentCoachPageProps) {
    const {
        tutors,
        onOpenMessages,
        openTutorId = null,
        columnWidth = PARENT_CONTENT_WIDTH,
        noTutor,
        onStartTrial,
        onAskQuestion,
        pausePeriod,
        loading = false,
        refreshing = false,
        onRefresh,
        error,
        onRetry,
        testID,
    } = props

    return (
        <PageTemplate
            refreshing={refreshing}
            onRefresh={onRefresh}
            testID={testID}
            loading={loading}
            skeleton={<StudentCoachSkeleton titleActions={onRefresh != null} />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={columnWidth}
        >
            <Stack gap="6">
                <Row justify="between" align="center">
                    <Title size="text-2xl">Messages</Title>
                    {onRefresh != null && <RefreshButton refreshing={refreshing} onRefresh={onRefresh} />}
                </Row>

                {noTutor != null && <EmptyState {...noTutorTexts(noTutor)} />}

                {onAskQuestion != null ? (
                    <HighlightFrame>
                        <TrialOfferCard onAskQuestion={onAskQuestion} pausePeriod={pausePeriod} />
                    </HighlightFrame>
                ) : onStartTrial != null ? (
                    <HighlightFrame>
                        <TrialOfferCard onStartTrial={onStartTrial} />
                    </HighlightFrame>
                ) : null}

                {tutors.length > 0 && (
                    <TutorTeamSection tutors={tutors} onOpenMessages={onOpenMessages} openTutorId={openTutorId} />
                )}
            </Stack>
        </PageTemplate>
    )
}
